/**
 * Header bar for the Schedule Interview wizard.
 * Shows title, candidate avatar + name, and an info icon that opens
 * a popover with the KContactCard (social links, email, phone, job/stage).
 *
 * Bulk-self-schedule (sch-31): when scheduling for multiple candidates
 * at once, the single-candidate row is replaced by a kula-ai
 * `<AvatarGroup max={3} showAllInPopover>` — first 3 avatars render
 * inline, the rest collapse into a "+N" chip whose click opens a
 * popover listing every candidate. Matches legacy
 * `KInterviewScheduler ScheduleHeader` `KAvatarList` parity for
 * `SCHEDULE_TYPE_BULK_SELF_SCHEDULE`.
 */
import { useMemo, useRef, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Avatar, AvatarGroup, Popover } from '@kula-ai/components';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { KContactCard } from '@/ui-library/components/organisms/KContactCard';

// Small grace period when leaving the trigger / content so the user can
// move the cursor between them without the popover snapping shut.
const HOVER_CLOSE_DELAY_MS = 120;

/**
 * Max avatars rendered inline in the bulk header; remainder collapses to a
 * "+N" chip whose click opens the kula-ai `AvatarGroup` popover with the
 * full candidate list (uses `showAllInPopover` so visible + overflow both
 * appear inside). 3 keeps the header readable in the wizard's narrow chrome.
 */
const BULK_AVATAR_VISIBLE_MAX = 3;

export interface ScheduleHeaderCandidate {
  name: string;
  avatar?: string;
  description?: string;
}

interface ScheduleHeaderProps {
  title: string;
  candidateId?: number;
  candidateName?: string;
  candidateAvatar?: string;
  /** When present (non-empty), header renders the bulk multi-candidate
   *  avatar stack instead of the single-candidate name + info popover. */
  candidates?: ScheduleHeaderCandidate[];
  jobTitle?: string;
  applicationData?: Record<string, unknown>;
  stageId?: number;
}

export const ScheduleHeader = ({
  title,
  candidateId,
  candidateName,
  candidateAvatar,
  candidates,
  jobTitle,
  applicationData,
  stageId,
}: ScheduleHeaderProps) => {
  const isBulk = Boolean(candidates && candidates.length > 0);
  // Pass applicationData.contact directly — KContactCard reads name,
  // position, email, emails, local_phone_number, image_url, social from it.
  const candidate = (applicationData?.contact ?? {}) as Record<string, any>;
  const currentJob = (applicationData?.ats_job ??
    (jobTitle ? { name: jobTitle } : {})) as Record<string, any>;

  const stageDetails = useMemo(() => {
    const stages = (applicationData?.ats_job_stages as any[]) || [];
    const currentStageId =
      (applicationData?.job_stage_id as number) || stageId || 0;
    const daysInStage = (applicationData?.days_in_stage as number) || 0;
    return { stages, stageId: currentStageId, daysInStage };
  }, [applicationData, stageId]);

  const hasContactData = !!candidate?.id;

  // Hover-trigger plumbing for the contact card popover. kula-ai
  // Popover is click-only out of the box; we drive `open` ourselves so
  // the card opens on mouse-enter and closes on mouse-leave, with a
  // small delay covering the trigger→content gap.
  const [contactCardOpen, setContactCardOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setContactCardOpen(false);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  return (
    <Box p="15px 20px" pb="0" flexShrink={0}>
      <Flex align="center" gap="10px">
        <Text
          color="secondary-900"
          fontSize="md"
          fontWeight="semibold"
          lineHeight="30px"
          flexShrink={0}
        >
          {title}
        </Text>
        {isBulk ? (
          <AvatarGroup
            max={BULK_AVATAR_VISIBLE_MAX}
            size="xs"
            showAllInPopover
            popoverContentClassName="w-80 p-0"
            renderPopoverContent={() => (
              <Box maxH="340px" overflowY="auto">
                {(candidates ?? []).map((c, i) => (
                  <Flex
                    key={`${c.name}-${i}`}
                    align="center"
                    gap="3"
                    py="1"
                    px="3"
                    borderBottom={
                      i < (candidates?.length ?? 0) - 1 ? '1px solid' : 'none'
                    }
                    borderColor="border-color"
                  >
                    <Avatar src={c.avatar} name={c.name} size="sm" />
                    <Flex flexDir="column" minW={0} flex="1">
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="secondary-900"
                        noOfLines={1}
                      >
                        {c.name}
                      </Text>
                      {c.description && (
                        <Text fontSize="xs" color="secondary-500" noOfLines={1}>
                          {c.description}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                ))}
              </Box>
            )}
          >
            {(candidates ?? []).map((c, i) => (
              <Avatar key={`${c.name}-${i}`} src={c.avatar} name={c.name} />
            ))}
          </AvatarGroup>
        ) : candidateName ? (
          <Flex align="center" gap="8px">
            <Avatar
              src={candidateAvatar}
              name={candidateName}
              size="xs"
              shape="square"
            />
            <Text
              fontSize="md"
              fontWeight="medium"
              color="secondary-900"
              noOfLines={1}
            >
              {candidateName}
            </Text>
            {hasContactData && (
              <Popover
                open={contactCardOpen}
                onOpenChange={setContactCardOpen}
                side="bottom"
                align="start"
                sideOffset={8}
                className="z-[9999] max-w-[350px] rounded-lg border border-gray-200 bg-white p-6 shadow-lg"
                trigger={
                  <Flex
                    cursor="pointer"
                    align="center"
                    justify="center"
                    flexShrink={0}
                    borderRadius="md"
                    p="1"
                    transition="background-color 0.15s ease"
                    aria-label="View candidate contact details"
                    onMouseEnter={() => {
                      cancelClose();
                      setContactCardOpen(true);
                    }}
                    onMouseLeave={scheduleClose}
                    onFocus={() => {
                      cancelClose();
                      setContactCardOpen(true);
                    }}
                    onBlur={scheduleClose}
                    _hover={{ bg: 'secondary.25' }}
                  >
                    <InfoCircle
                      width={18}
                      height={18}
                      color="var(--chakra-colors-secondary-500)"
                    />
                  </Flex>
                }
              >
                <Box onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
                  <KContactCard
                    contactId={candidateId ?? 0}
                    candidate={candidate}
                    currentJob={currentJob}
                    stageDetails={stageDetails}
                  />
                </Box>
              </Popover>
            )}
          </Flex>
        ) : null}
      </Flex>
    </Box>
  );
};
